import type { LabConfig, TestResult } from '../types';
import { loadWasmCache } from '../wasm';
import { computeStats, now } from '../utils';
import { createHighResClock, measureWithClock } from '../utils/clock';

const GROUP = 'J_cache_tlb';
const KB = 1024;
const MB = 1024 * KB;

type Point = { x: number; y: number; label: string };

function makeCurve(name: string, curve: Point[], extra?: Record<string, unknown>): TestResult {
  return {
    name,
    group: GROUP,
    success: curve.length > 0,
    durationMs: 0,
    samples: curve.map((p) => p.y),
    stats: computeStats(curve.map((p) => p.y)),
    metadata: { curve, ...extra },
  };
}

export async function runCacheTlbTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];
  if (!config.enableWASM) return tests;

  const fast = config.cacheMode === 'fast';
  const runs = config.cacheRuns ?? (fast ? 1 : 2);
  const warmup = config.cacheWarmup ?? (fast ? 0 : 1);
  const iterations = config.cacheIterations ?? (fast ? 300_000 : undefined);

  let wasm: Awaited<ReturnType<typeof loadWasmCache>>;
  try {
    wasm = await loadWasmCache('/cache.wasm');
  } catch (err) {
    return [{ name: 'cache_load', group: GROUP, success: false, durationMs: now() - t0, error: `WASM load failed: ${err instanceof Error ? err.message : String(err)}` }];
  }

  // 256 MB initial memory is enough for 64 MB chain + 64 MB index buffer.
  const neededPages = 4096;
  if (wasm.exports.memory.buffer.byteLength < neededPages * 64 * KB) {
    wasm.exports.memory.grow(neededPages - wasm.exports.memory.buffer.byteLength / (64 * KB));
  }

  const startPtr = 1 * MB;
  const indicesPtr = 128 * MB;

  let clock: Awaited<ReturnType<typeof createHighResClock>> = null;
  if (config.mode !== 'prod') {
    try { clock = await createHighResClock(100); } catch { clock = null; }
  }

  const measure = (fn: () => void) => {
    if (clock) return measureWithClock(clock, fn, runs, warmup);
    for (let i = 0; i < warmup; i++) fn();
    const samples: number[] = [];
    for (let i = 0; i < runs; i++) { const t0 = now(); fn(); samples.push(now() - t0); }
    return samples;
  };

  try {
    const sizes = config.cacheSizes ?? (fast
      ? [64 * KB, 256 * KB, 1 * MB, 8 * MB]
      : [4 * KB, 8 * KB, 16 * KB, 24 * KB, 32 * KB, 48 * KB, 64 * KB, 96 * KB, 128 * KB, 192 * KB, 256 * KB, 384 * KB, 512 * KB, 768 * KB, 1 * MB, 1.5 * MB, 2 * MB, 4 * MB, 8 * MB, 16 * MB, 32 * MB, 64 * MB]);
    tests.push(makeCurve('cache_size_random', runCacheLadder(wasm, startPtr, indicesPtr, sizes, 4, iterations, measure), { sizes }));
  } catch (err) {
    tests.push({ name: 'cache_size_random', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  try {
    const associativities = config.cacheAssociativities ?? (fast ? [4, 16] : Array.from({ length: 16 }, (_, i) => i + 1));
    tests.push(makeCurve('cache_associativity_32k', runAssociativityLadder(wasm, startPtr, indicesPtr, associativities, 32 * KB, iterations, measure), { associativities }));
  } catch (err) {
    tests.push({ name: 'cache_associativity_32k', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  try {
    const pageCounts = config.cachePageCounts ?? (fast ? [8] : Array.from({ length: 16 }, (_, i) => (i + 1) * 8));
    const sizes = pageCounts.map((p) => p * 4 * KB);
    tests.push(makeCurve('tlb_size', runTlbLadder(wasm, startPtr, sizes, iterations, measure), { pageCounts }));
  } catch (err) {
    tests.push({ name: 'tlb_size', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  if (clock) clock.terminate();
  return tests;
}

function shuffleIndices(view: Int32Array, count: number) {
  for (let i = 0; i < count; i++) view[i] = i;
  for (let i = count - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [view[i], view[j]] = [view[j], view[i]];
  }
}

function runCacheLadder(wasm: Awaited<ReturnType<typeof loadWasmCache>>, startPtr: number, indicesPtr: number, sizes: number[], stride: number, fixedIterations: number | undefined, measure: (fn: () => void) => number[]): Point[] {
  const maxCount = 16 * 1024 * 1024;
  const view = new Int32Array(wasm.exports.memory.buffer, indicesPtr, maxCount);
  const curve: Point[] = [];

  for (const size of sizes) {
    const count = size / stride;
    if (count < 2 || count > maxCount) continue;

    shuffleIndices(view, count);
    wasm.buildChainFromIndices(startPtr, count, stride, indicesPtr);

    const iterations = fixedIterations ?? Math.min(2_500_000, Math.max(1_000_000, Math.floor(64 * 1024 * 1024 / size) * 100_000));
    const samples = measure(() => wasm.iterate(startPtr, BigInt(iterations)));
    const ns = (computeStats(samples).median * 1_000_000) / iterations;
    curve.push({ x: size, y: ns, label: `${(size / KB).toFixed(1)}KB` });
  }
  return curve;
}

function runAssociativityLadder(wasm: Awaited<ReturnType<typeof loadWasmCache>>, startPtr: number, indicesPtr: number, associativities: number[], stride: number, fixedIterations: number | undefined, measure: (fn: () => void) => number[]): Point[] {
  const maxCount = 32;
  const view = new Int32Array(wasm.exports.memory.buffer, indicesPtr, maxCount);
  const iterations = fixedIterations ?? 5_000_000;
  const curve: Point[] = [];

  for (const assoc of associativities) {
    if (assoc < 2 || assoc > maxCount) continue;
    shuffleIndices(view, assoc);
    wasm.buildChainFromIndices(startPtr, assoc, stride, indicesPtr);
    const samples = measure(() => wasm.iterate(startPtr, BigInt(iterations)));
    const ns = (computeStats(samples).median * 1_000_000) / iterations;
    curve.push({ x: assoc, y: ns, label: `${assoc}w` });
  }
  return curve;
}

function runTlbLadder(wasm: Awaited<ReturnType<typeof loadWasmCache>>, startPtr: number, sizes: number[], fixedIterations: number | undefined, measure: (fn: () => void) => number[]): Point[] {
  const iterations = fixedIterations ?? 5_000_000;
  const indicesPtr = 128 * MB;
  const maxCount = 16 * 1024 * 1024;
  const view = new Int32Array(wasm.exports.memory.buffer, indicesPtr, maxCount);
  const curve: Point[] = [];

  for (const size of sizes) {
    const count = Math.floor(size / (4 * KB));
    if (count < 2) continue;
    shuffleIndices(view, count);
    wasm.buildChainFromIndices(startPtr, count, 4 * KB, indicesPtr);
    const samples = measure(() => wasm.iterate(startPtr, BigInt(iterations)));
    const ns = (computeStats(samples).median * 1_000_000) / iterations;
    curve.push({ x: size, y: ns, label: `${(size / (4 * KB)).toFixed(0)}p` });
  }
  return curve;
}
